import api from '.'

/**
 * The AdminOrganizationUsersApi class provides methods for interacting with user data in an organization's admin context.
 * This includes retrieving lists of users and detailed information about specific users within a given organization.
 */
class AdminOrganizationUsersApi {
    /**
     * Fetches the list of users for a given organization.
     *
     * @param {string} organization_id - The ID of the organization for which the user list is to be fetched.
     * @returns {Promise<Object>} A promise that resolves to the data containing the list of users.
     */
    list(organization_id) {
        const payload = {
            organization_id
        }

        return api
            .sentRequest('/api/organization/admin/user/list', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Sends an API request to retrieve user information for a specific organization.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} user_id - The ID of the user whose information is to be retrieved.
     * @returns {Promise<Object>} A promise that resolves to the user information.
     */
    info(organization_id, user_id) {
        const payload = {
            organization_id,
            user_id
        }

        return api
            .sentRequest('/api/organization/admin/user/info', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Creates a new user within the specified organization.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} user_permission_type - The permission type for the user.
     * @param {string} usr_first_name - The first name of the user.
     * @param {string} usr_last_name - The last name of the user.
     * @param {string} usr_email - The email address of the user.
     * @param {Array<string>} organization_account_ids - The IDs of the organization accounts.
     * @param {string} mail_text_plain - The plain text for the email.
     * @returns {Promise<Object>} The response data from the API.
     */
    create(
        organization_id,
        user_permission_type,
        usr_first_name,
        usr_last_name,
        usr_email,
        organization_account_ids,
        mail_text_plain
    ) {
        const payload = {
            organization_id,
            user_permission_type,
            usr_first_name,
            usr_last_name,
            usr_email,
            organization_account_ids,
            mail_text_plain
        }

        return api
            .sentRequest('/api/organization/admin/user/create', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Updates user permissions in an organization.
     *
     * @param {string} organization_id - The ID of the organization the user belongs to.
     * @param {string} user_id - The ID of the user whose permissions are being updated.
     * @param {string} user_permission_type - The type of permission to be assigned to the user.
     * @param {Array<string>} organization_account_ids - The list of organization account IDs the user has access to.
     * @returns {Promise<Object>} The response data from the API, or the catch response in case of an error.
     */
    update(
        organization_id,
        user_id,
        user_permission_type,
        organization_account_ids
    ) {
        const payload = {
            organization_id,
            user_id,
            user_permission_type,
            organization_account_ids
        }

        return api
            .sentRequest('/api/organization/admin/user/update', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Sends an API request to delete a user from an organization.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} user_id - The ID of the user to be deleted.
     * @returns {Promise<Object>} The response data from the API.
     */
    delete(organization_id, user_id) {
        const payload = {
            organization_id,
            user_id
        }

        return api
            .sentRequest('/api/organization/admin/user/delete', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Retrieves the account permission users for a given organization and account.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} organization_account_id - The ID of the organization's account.
     * @returns {Promise<Object>} A promise resolving to the data containing account permission users.
     */
    infoAccountPermissionUsers(organization_id, organization_account_id) {
        const payload = {
            organization_id,
            organization_account_id
        }

        return api
            .sentRequest(
                '/api/organization/admin/user/account_permissions',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Fetches information related to an invitation using the provided invite reference ID.
     *
     * @param {string} invite_ref_id - The reference ID of the invitation.
     * @returns {Promise<Object>} A promise that resolves to the invitation information.
     */
    inviteInfo(invite_ref_id) {
        const payload = {
            invite_ref_id
        }

        return api
            .sentRequest('/api/organization/user/invite/info', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Sends a request to join an organization using an invite reference ID.
     *
     * @param {string} invite_ref_id - The reference ID for the invite.
     * @returns {Promise<Object>} The response data from the API.
     */
    inviteJoin(invite_ref_id) {
        const payload = {
            invite_ref_id
        }

        return api
            .sentRequest('/api/organization/user/invite/join', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Approves an invitation for a user to join an organization.
     *
     * @param {string} invite_ref_id - The reference ID of the invitation to approve.
     * @returns {Promise<Object>} The response data from the API upon successful approval.
     */
    inviteApprove(invite_ref_id) {
        const payload = {
            invite_ref_id
        }

        return api
            .sentRequest('/api/organization/user/invite/approve', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Rejects an invitation by sending a request to the server with the provided invite reference ID.
     *
     * @param {string} invite_ref_id - The reference ID of the invitation to be rejected.
     * @returns {Promise<Object>} The response data from the server if the request is successful, or a catch response if there
     *                            is an error.
     */
    inviteReject(invite_ref_id) {
        const payload = {
            invite_ref_id
        }

        return api
            .sentRequest('/api/organization/user/invite/reject', payload, true)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }
}

export const adminOrganizationUsersApi = new AdminOrganizationUsersApi()
